import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CFormProvider from "../../components/HookFormComponents/CFormProvider";
import CFormTextField from "../../components/HookFormComponents/CFormTextField";
import CButton from "../../components/CButton";
import { ReactComponent as EditIcon } from "../../assets/images/edit-icon.svg";
import CTextField from "../../components/CTextField";
import CSelect from "../../components/CSelect";
import { POPULATION_GROUPS, EAP_REGIONS } from "../../constant/constants";
import {
  fetchCompanyUserDemographics,
  saveBBBEEBudgetConfiguration,
  fetchEAPConfiguration,
  fetchBBBEEBudgetConfiguration,
} from "../../slices/b_bbeeConfigurationSlice";
import { fetchYearId } from "../../slices/yearSlice";
import {
  setTotalBudget,
  fetchTotalBudget,
  saveTotalBudget,
} from "../../slices/totalBudgetSlice";
import { fromWithCompany, supabase } from "../../config/supabaseClient";
import { toast } from "react-toastify";
import { formatZAR } from "../../constant/utils/commonUtils";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";

const BBBudgetConfiguration = () => {
  const dispatch = useDispatch();
  const {
    demographics,
    loading,
    error,
    eapConfiguration = [],
    eapLoading = false,
    eapError = null,
    budgetConfiguration,
  } = useSelector((state) => state.bBbeeConfiguration);
  const { totalBudget } = useSelector((state) => state.totalBudget);

  const methods = useForm({
    defaultValues: {
      totalEmployees: 100,
      totalBudget: "",
    },
  });
  const companyId = JSON.parse(localStorage.getItem("sdfUser"))?.company_id;

  // State for Company Demographics data (read-only from company_user table)
  const [demographicsData, setDemographicsData] = useState([]);

  // State for Budget Allocation data (editable)
  const [budgetAllocationData, setBudgetAllocationData] = useState([]);

  // State for Disability (separate from other groups)
  const [disabilityData, setDisabilityData] = useState({
    randTotal: "0",
  });

  // None / unemployed branch budget (same shape as disability; stored under demographic_config.none)
  const [noneData, setNoneData] = useState({
    randTotal: "0",
  });

  // State for EAP selection and data
  const [selectedEAPRegion, setSelectedEAPRegion] = useState("National");
  const [yearId, setYearId] = useState(null);

  // Fetch demographics data on component mount
  useEffect(() => {
    dispatch(fetchCompanyUserDemographics(companyId));
    dispatch(fetchYearId()).then((res) => setYearId(res.payload));
  }, [dispatch]);

  // Fetch total budget from database when yearId is available
  useEffect(() => {
    if (companyId && yearId) {
      dispatch(fetchTotalBudget({ yearId, companyId }));
    }
  }, [companyId, yearId, dispatch]);

  // Fetch existing BBBEE budget configuration when yearId and companyId are available
  useEffect(() => {
    if (companyId && yearId) {
      dispatch(fetchBBBEEBudgetConfiguration({ yearId, companyId }));
    }
  }, [companyId, yearId, dispatch]);

  // Update demographics data when demographics change (read-only from company_user table)
  useEffect(() => {
    if (demographics && Object.keys(demographics).length > 0) {
      const newDemographicsData = POPULATION_GROUPS.map((group) => {
        const groupData = demographics[group.key] || {
          male: 0,
          female: 0,
          total: 0,
        };
        return {
          group: group.key,
          headCountM: groupData.male.toString(),
          headCountF: groupData.female.toString(),
          headCountTotal: groupData.total.toString(),
        };
      });
      setDemographicsData(newDemographicsData);
    }
  }, [demographics]);

  // Initialize budget allocation rows once demographics exist. Do not reset when `demographicsData`
  // gets a new array reference later (same Redux data) — that was wiping server-loaded budget % and
  // head counts until a full page refresh.
  useEffect(() => {
    if (demographicsData.length === 0) return;
    if (budgetAllocationData.length > 0) return;

    const newBudgetAllocationData = POPULATION_GROUPS.map((group) => ({
      group: group.key,
      randM: "0",
      randF: "0",
      randTotal: "0",
      headCountM: "0",
      headCountF: "0",
      headCountTotal: "0",
    }));
    setBudgetAllocationData(newBudgetAllocationData);
  }, [demographicsData, budgetAllocationData.length]);

  // Load existing configuration data into budget allocation table when fetched
  useEffect(() => {
    if (
      budgetConfiguration &&
      budgetConfiguration.demographic_config &&
      budgetAllocationData.length > 0
    ) {
      const config = budgetConfiguration.demographic_config;

      // Set EAP region if available
      if (config.eap_target_type) {
        setSelectedEAPRegion(config.eap_target_type);
      }

      // Load Disability data separately
      if (config.disability) {
        setDisabilityData({
          randTotal: config.disability.budget?.total?.toString() || "0",
        });
      }

      if (config.none) {
        setNoneData({
          randTotal: config.none.budget?.total?.toString() || "0",
        });
      }

      // Update budget allocation data with existing configuration
      if (config.config && Array.isArray(config.config)) {
        const updatedBudgetAllocationData = budgetAllocationData.map((row) => {
          // For other groups, load from config array
          const existingConfig = config.config.find(
            (item) => item.race === row.group.toLowerCase()
          );
          if (existingConfig) {
            return {
              ...row,
              randM: existingConfig.budget?.male?.toString() || "0",
              randF: existingConfig.budget?.female?.toString() || "0",
              randTotal: (
                parseFloat(existingConfig.budget?.male || 0) +
                parseFloat(existingConfig.budget?.female || 0)
              ).toString(),
              headCountM:
                existingConfig.headCount?.male?.toString() || row.headCountM,
              headCountF:
                existingConfig.headCount?.female?.toString() || row.headCountF,
              headCountTotal: (
                (parseInt(existingConfig.headCount?.male) || 0) +
                (parseInt(existingConfig.headCount?.female) || 0)
              ).toString(),
            };
          }
          return row;
        });
        setBudgetAllocationData(updatedBudgetAllocationData);
      }
    }
  }, [budgetConfiguration, budgetAllocationData.length]);

  // Fetch EAP data when region is selected
  useEffect(() => {
    if (selectedEAPRegion) {
      const selectedRegion = EAP_REGIONS.find(
        (region) => region.name === selectedEAPRegion
      );
      if (selectedRegion) {
        dispatch(
          fetchEAPConfiguration({
            provinceName: selectedRegion.isNational
              ? null
              : selectedRegion.name,
            isNational: selectedRegion.isNational,
          })
        );
      }
    }
  }, [selectedEAPRegion, dispatch]);

  // Calculate totals for demographics data (Company Demographics table)
  const demographicsTotals = demographicsData.reduce(
    (acc, row) => {
      acc.headCountM += parseInt(row.headCountM) || 0;
      acc.headCountF += parseInt(row.headCountF) || 0;
      acc.headCountTotal += parseInt(row.headCountTotal) || 0;
      return acc;
    },
    {
      headCountM: 0,
      headCountF: 0,
      headCountTotal: 0,
    }
  );

  // Calculate totals for budget allocation data (Budget Allocation table)
  const budgetTotals = budgetAllocationData.reduce(
    (acc, row) => {
      acc.randM += parseFloat(row.randM) || 0;
      acc.randF += parseFloat(row.randF) || 0;
      acc.randTotal += parseFloat(row.randTotal) || 0;
      acc.headCountM += parseInt(row.headCountM) || 0;
      acc.headCountF += parseInt(row.headCountF) || 0;
      acc.headCountTotal += parseInt(row.headCountTotal) || 0;
      return acc;
    },
    {
      randM: 0,
      randF: 0,
      randTotal: 0,
      headCountM: 0,
      headCountF: 0,
      headCountTotal: 0,
    }
  );

  // Total Black Budget (African, Coloured, Indian) and Total Other Budget (White, Foreigner) - from POPULATION_GROUPS
  const blackGroupKeys = POPULATION_GROUPS.filter((g) => g.category === "black").map((g) => g.key);
  const otherGroupKeys = POPULATION_GROUPS.filter((g) => g.category === "other").map((g) => g.key);
  const totalBlackPercentage = budgetAllocationData
    .filter((row) => blackGroupKeys.includes(row.group))
    .reduce((sum, row) => sum + (parseFloat(row.randTotal) || 0), 0);
  const totalOtherPercentage = budgetAllocationData
    .filter((row) => otherGroupKeys.includes(row.group))
    .reduce((sum, row) => sum + (parseFloat(row.randTotal) || 0), 0);
  const totalBudgetValue = parseFloat(totalBudget) || 0;
  const totalBlackBudgetRand = (totalBlackPercentage / 100) * totalBudgetValue;
  const totalOtherBudgetRand = (totalOtherPercentage / 100) * totalBudgetValue;
  const blackBudgetLabel = POPULATION_GROUPS.filter((g) => g.category === "black").map((g) => g.label).join(", ");
  const otherBudgetLabel = POPULATION_GROUPS.filter((g) => g.category === "other").map((g) => g.label).join(", ");

  // Handle input changes
  const handleInputChange = (rowIndex, field, value) => {
    const currentRow = budgetAllocationData[rowIndex];

    // Validate percentage input for M and F columns in Budget table
    if (field === "randM" || field === "randF") {
      // Only allow numbers and decimal points
      const regex = /^\d*\.?\d*$/;
      if (!regex.test(value) && value !== "") {
        return;
      }

      // Limit to 2 decimal places
      const parts = value.split(".");
      if (parts[1] && parts[1].length > 2) {
        return;
      }

      // Limit to 100%
      const numValue = parseFloat(value) || 0;
      if (numValue > 100) {
        return;
      }
    }

    // Validate integer input for Head Count M and F columns
    if (field === "headCountM" || field === "headCountF") {
      // Only allow numbers (no decimal points)
      const regex = /^\d*$/;
      if (!regex.test(value) && value !== "") {
        return;
      }
    }

    const newData = [...budgetAllocationData];
    newData[rowIndex] = { ...newData[rowIndex], [field]: value };

    // Auto-calculate totals for each row based on field type
    if (field === "randM" || field === "randF") {
      // If editing Rand values, calculate total
      const randM = parseFloat(newData[rowIndex].randM) || 0;
      const randF = parseFloat(newData[rowIndex].randF) || 0;
      newData[rowIndex].randTotal = (randM + randF).toFixed(2);
    } else if (field === "malePercentage" || field === "femalePercentage") {
      // If editing percentage values, calculate total percentage
      const malePercentage = parseFloat(newData[rowIndex].malePercentage || 0);
      const femalePercentage = parseFloat(
        newData[rowIndex].femalePercentage || 0
      );
      newData[rowIndex].totalPercentage = (
        malePercentage + femalePercentage
      ).toFixed(1);

      // Also update Rand values based on percentages
      const totalBudget = budgetTotals.randTotal || 1;
      const maleRand = Math.round((malePercentage / 100) * totalBudget);
      const femaleRand = Math.round((femalePercentage / 100) * totalBudget);
      newData[rowIndex].randM = maleRand.toString();
      newData[rowIndex].randF = femaleRand.toString();
      newData[rowIndex].randTotal = (maleRand + femaleRand).toString();
    } else if (field === "totalPercentage") {
      // If editing total percentage, distribute between M and F (50/50 split)
      const totalPercentage = parseFloat(value) || 0;
      const halfPercentage = totalPercentage / 2;
      newData[rowIndex].malePercentage = halfPercentage.toFixed(1);
      newData[rowIndex].femalePercentage = halfPercentage.toFixed(1);

      // Also update Rand values based on total percentage
      const totalBudget = budgetTotals.randTotal || 1;
      const totalRand = Math.round((totalPercentage / 100) * totalBudget);
      const halfRand = Math.floor(totalRand / 2);
      newData[rowIndex].randM = halfRand.toString();
      newData[rowIndex].randF = (totalRand - halfRand).toString();
      newData[rowIndex].randTotal = totalRand.toString();
    }

    if (field === "headCountM" || field === "headCountF") {
      const headCountM = parseInt(newData[rowIndex].headCountM) || 0;
      const headCountF = parseInt(newData[rowIndex].headCountF) || 0;
      newData[rowIndex].headCountTotal = (headCountM + headCountF).toString();
    }

    // Validate total budget doesn't exceed 100
    const newTotal = newData.reduce(
      (sum, row) => sum + (parseFloat(row.randTotal) || 0),
      0
    );
    if (newTotal > 100) {
      toast.error("Total budget cannot exceed 100%");
      return; // Don't update the state if total exceeds 100
    }
    setBudgetAllocationData(newData);
  };

  // Handle EAP region selection
  const handleEAPRegionChange = (event) => {
    setSelectedEAPRegion(event.target.value);
  };

  const onSubmit = async (data) => {
    try {
      if (!companyId || !yearId) {
        toast.error("Company ID or Year ID not available");
        return;
      }

      // Validate total budget equals 100 (allowing for floating point rounding)
      const totalBudgetPercentage = budgetAllocationData.reduce(
        (sum, row) => sum + (parseFloat(row.randTotal) || 0),
        0
      );
      const roundedTotalBudgetPercentage = parseFloat(
        totalBudgetPercentage.toFixed(2)
      );
      if (Math.abs(roundedTotalBudgetPercentage - 100) > 0.01) {
        toast.error(
          `Total budget must equal 100. Current total: ${roundedTotalBudgetPercentage.toFixed(
            2
          )}`
        );
        return;
      }

      // Get Disability data from separate state
      const disabilityBudget = parseFloat(disabilityData.randTotal) || 0;
      const noneBudget = parseFloat(noneData.randTotal) || 0;
      const totalBudgetValue = parseFloat(totalBudget) || 0;

      // Validate disability budget does not exceed total budget
      if (disabilityBudget > totalBudgetValue) {
        toast.error(
          `Disability budget (${formatZAR(
            disabilityBudget
          )}) cannot exceed Total Budget (${formatZAR(totalBudgetValue)})`
        );
        return;
      }

      if (noneBudget > totalBudgetValue) {
        toast.error(
          `None (unemployed) budget (${formatZAR(
            noneBudget
          )}) cannot exceed Total Budget (${formatZAR(totalBudgetValue)})`
        );
        return;
      }

      // Validate B-BBEE total budget equals Department Total Budget
      // Fetch department config to calculate department total
      const { data: configData, error: configError } = await supabase
        .from("configurations")
        .select("department_config, total_budget")
        .eq("company_id", companyId)
        .eq("year_id", yearId)
        .maybeSingle();

      if (configError) {
        console.error("Error fetching department config:", configError);
        toast.error("Error validating budgets. Please try again.");
        return;
      }

      if (configData?.department_config) {
        // Calculate sum of all department budgets
        const departmentTotal = configData.department_config.reduce(
          (sum, dept) => sum + (parseFloat(dept.budget) || 0),
          0
        );

        // Validate that B-BBEE total budget equals Department Total Budget
        if (Math.abs(totalBudgetValue - departmentTotal) > 0.01) {
          toast.error(
            `Department Total Budget (${formatZAR(
              departmentTotal
            )}) and B-BBEE Total Budget (${formatZAR(
              totalBudgetValue
            )}) must align.`
          );
          return;
        }
      }

      const disabilityDataObj = {
        budget: {
          total: disabilityBudget,
        },
      };

      const noneDataObj = {
        budget: {
          total: noneBudget,
        },
      };

      // Transform data to match the required format for demographic_config
      const demographicConfig = {
        eap_target_type: selectedEAPRegion || "National",
        config: POPULATION_GROUPS.map((group) => {
          const budgetRow = budgetAllocationData.find(
            (row) => row.group === group.key
          );

          return {
            race: group.key.toLowerCase(),
            headCount: {
              male: budgetRow?.headCountM || 0,
              female: budgetRow?.headCountF || 0,
              other: 0,
            },
            budget: {
              male: budgetRow?.randM || 0,
              female: budgetRow?.randF || 0,
              other: 0,
            },
          };
        }),
        ...(disabilityDataObj && { disability: disabilityDataObj }),
        ...(noneDataObj && { none: noneDataObj }),
      };

      await dispatch(
        saveBBBEEBudgetConfiguration({
          budgetData: {
            demographic_config: demographicConfig,
            total_budget: totalBudget,
          },
          yearId,
          companyId,
        })
      );

      toast.success("BBBEE Budget Configuration saved successfully!");
    } catch (error) {
      console.error("Error saving configuration:", error);
      toast.error(
        "Failed to save BBBEE Budget Configuration: " +
          (error.message || "Unknown error")
      );
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 border border-blue-200 relative mt-8 flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <div className="w-[calc(60%-4px)]">
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <h2 className="text-center text-base font-semibold p-2 bg-white min-h-[56px] flex items-center justify-center">
              Company Demographics
            </h2>

            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-gray-600">
                  Loading demographics...
                </span>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded p-3 m-4">
                <p className="text-red-600 text-sm">
                  Error loading demographics: {error}
                </p>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-blue-50 font-semibold border-b border-t border-gray-200">
                    <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200 w-4"></th>
                    <th
                      colSpan={3}
                      className=" font-semibold px-4 py-2 text-center bg-gray-150 border-l-0 text-xs leadimg-xs"
                    >
                      Head Count
                    </th>
                    <th className="font-semibold px-4 py-2 bg-white w-3"></th>
                    <th
                      className="font-semibold  px-4 py-2 text-center bg-gray-150 border-r-blue-50 text-xs leadin-xs"
                      colSpan={3}
                    >
                      %
                    </th>
                  </tr>
                  <tr className="bg-blue-50 font-semibold border-b border-gray-200">
                    <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200"></th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Male
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Female
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center  text-xs leadin-xs">
                      Total
                    </th>
                    <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200"></th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Male
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Female
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {POPULATION_GROUPS.map((group, idx) => {
                    const row = demographicsData.find(
                      (r) => r.group === group.key
                    ) || {
                      group: group.key,
                      headCountM: "0",
                      headCountF: "0",
                      headCountTotal: "0",
                    };
                    const totalEmployees =
                      demographicsTotals.headCountTotal || 1;
                    const malePercentage = (
                      ((parseInt(row.headCountM) || 0) / totalEmployees) *
                      100
                    ).toFixed(1);
                    const femalePercentage = (
                      ((parseInt(row.headCountF) || 0) / totalEmployees) *
                      100
                    ).toFixed(1);
                    const totalPercentage = (
                      ((parseInt(row.headCountTotal) || 0) / totalEmployees) *
                      100
                    ).toFixed(1);

                    return (
                      <tr
                        key={row.group}
                        className="text-center border-b border-gray-200"
                      >
                        <td className=" px-4 py-2 text-left bg-white border-l-0 text-xs leadin-xs font-medium">
                          {row.group}
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          {row.headCountM}
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          {row.headCountF}
                        </td>
                        <td className=" px-4 py-2 bg-gray-50 text-xs leadin-xs">
                          {row.headCountTotal}
                        </td>
                        <td className="px-4 py-2 bg-white border-t border-b border-gray-200"></td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          {malePercentage}%
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          {femalePercentage}%
                        </td>
                        <td className=" px-4 py-2 bg-gray-50 text-xs leadin-xs">
                          {totalPercentage}%
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="font-semibold bg-blue-50">
                    <td className=" px-4 py-2  text-xs leadin-xs border-l-blue-100">
                      Total
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {demographicsTotals.headCountM}
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {demographicsTotals.headCountF}
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {Number(demographicsTotals.headCountTotal)}
                    </td>
                    <td className="px-4 py-2 bg-white  border-gray-200"></td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {demographicsTotals.headCountTotal
                        ? (
                            (demographicsTotals.headCountM /
                              demographicsTotals.headCountTotal) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {demographicsTotals.headCountTotal
                        ? (
                            (demographicsTotals.headCountF /
                              demographicsTotals.headCountTotal) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      100.0%
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="w-[calc(40%-4px)]">
          <div className="rounded-xl  overflow-hidden border border-gray-200 h-full">
            <div className="flex justify-between items-center gap-2 p-2 px-3 configuration-table-header">
              <h2 className="text-center flex-1 text-base font-semibold">
                EAP Target Decided (National/ Provincial)
              </h2>

              {/* EAP Region Selector */}
              <CSelect
                name="eapRegion"
                value={selectedEAPRegion}
                onChange={handleEAPRegionChange}
                options={EAP_REGIONS.map((region) => ({
                  value: region.name,
                  label: region.name,
                }))}
                placeholder="Select EAP Region..."
                formControlClass="eap-region-select"
              />
            </div>

            {eapLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-gray-600">Loading EAP data...</span>
              </div>
            ) : eapError ? (
              <div className="bg-red-50 border border-red-200 rounded p-3 m-4">
                <p className="text-red-600 text-sm">
                  Error loading EAP data: {eapError}
                </p>
              </div>
            ) : !selectedEAPRegion ? (
              <div className="flex justify-center items-center py-8">
                <span className="text-gray-500">
                  Please select an EAP region to view data
                </span>
              </div>
            ) : !eapConfiguration || eapConfiguration.length === 0 ? (
              <div className="flex justify-center items-center h-[calc(100%-56px)]">
                <span className="text-gray-500">
                  No EAP data available for selected region
                </span>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-blue-50 font-semibold border-b border-t border-gray-200">
                    <th className="w-[165px] font-semibold  px-4 py-2 text-left bg-white border-l-0 text-xs leadin-xs">
                      Population Group
                    </th>
                    <th
                      className="font-semibold  px-4 py-2 text-center border-r-blue-50 text-xs leadin-xs"
                      colSpan={3}
                    >
                      %
                    </th>
                  </tr>
                  <tr className="bg-blue-100 font-semibold border-b  border-gray-200">
                    <th className="font-semibold  px-4 py-2 bg-white border-l-0"></th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      M
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      F
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center border-r-blue-100 text-xs leadin-xs">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {POPULATION_GROUPS.map((group) => {
                    const groupData =
                      eapConfiguration.find(
                        (item) => item.race === group.key
                      ) || {};
                    const malePercentage = parseFloat(
                      groupData.male_percentage || 0
                    );
                    const femalePercentage = parseFloat(
                      groupData.female_percentage || 0
                    );
                    const totalPercentage = malePercentage + femalePercentage;

                    return (
                      <tr
                        key={group.key}
                        className="text-center border-b  border-gray-200"
                      >
                        <td className=" px-4 py-2 text-left bg-white border-l-0 text-xs leadin-xs font-medium">
                          {group.label}
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          {malePercentage}%
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          {femalePercentage}%
                        </td>
                        <td className=" px-4 py-2 bg-gray-50 border-r-blue-100 text-xs leadin-xs">
                          {totalPercentage}%
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="font-semibold bg-blue-100 ">
                    <td className=" px-4 py-2  text-xs leadin-xs border-b-0 border-l-blue-100">
                      Total
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs border-b-0 text-center">
                      {eapConfiguration
                        .reduce(
                          (sum, item) =>
                            sum + parseFloat(item.male_percentage || 0),
                          0
                        )
                        .toFixed(1)}
                      %
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs border-b-0 text-center">
                      {eapConfiguration
                        .reduce(
                          (sum, item) =>
                            sum + parseFloat(item.female_percentage || 0),
                          0
                        )
                        .toFixed(1)}
                      %
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs border-b-0 border-r-blue-100 text-center">
                      {eapConfiguration
                        .reduce(
                          (sum, item) =>
                            sum +
                            parseFloat(item.male_percentage || 0) +
                            parseFloat(item.female_percentage || 0),
                          0
                        )
                        .toFixed(1)}
                      %
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="w-full">
          {/* <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
          <h2 className="text-center text-base font-semibold p-2 bg-white">
            Budget Configuration
          </h2>
          <div className="p-4">
            <CFormProvider methods={methods} onSubmit={onSubmit}>
              <div className="flex gap-4">
                <CFormTextField
                  name="totalEmployees"
                  label="Total Employees"
                  value={demographicsTotals.headCountTotal || 0}
                  disabled={true}
                  className="w-1/2"
                />
                
              </div>
            </CFormProvider>
          </div>
        </div> */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <h2 className="text-center text-base font-semibold p-2 bg-white ">
              Budget Allocation
            </h2>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-gray-600">Loading...</span>
              </div>
            ) : (
              <table className="w-full border-collapse configuration-table">
                <thead>
                  <tr className="bg-blue-50 font-semibold border-b border-t border-gray-200">
                    <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200 w-4"></th>
                    <th
                      colSpan={3}
                      className=" font-semibold px-4 py-2 text-center bg-gray-150 border-l-0 text-xs leadin-xs"
                    >
                      Budget
                    </th>
                    <th className="font-semibold px-4 py-2 bg-white w-3"></th>
                    <th
                      className="font-semibold  px-4 py-2 text-center bg-gray-150 border-r-blue-50 text-xs leadin-xs"
                      colSpan={3}
                    >
                      Head Count
                    </th>
                  </tr>
                  <tr className="bg-blue-50 font-semibold border-b border-gray-200">
                    <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200"></th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Male (%)
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Female (%)
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center  text-xs leadin-xs">
                      Total
                    </th>
                    <th className="font-semibold px-4 py-2 bg-white border-t border-b border-gray-200"></th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Male
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Female
                    </th>
                    <th className="font-semibold  px-4 py-2 text-center text-xs leadin-xs">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {POPULATION_GROUPS.map((group, idx) => {
                    const row = budgetAllocationData.find(
                      (r) => r.group === group.key
                    ) || {
                      group: group.key,
                      randM: "0",
                      randF: "0",
                      randTotal: "0",
                      headCountM: "0",
                      headCountF: "0",
                      headCountTotal: "0",
                    };
                    const totalEmployees =
                      demographicsTotals.headCountTotal || 1;
                    const malePercentage = (
                      ((parseInt(row.headCountM) || 0) / totalEmployees) *
                      100
                    ).toFixed(1);
                    const femalePercentage = (
                      ((parseInt(row.headCountF) || 0) / totalEmployees) *
                      100
                    ).toFixed(1);
                    const totalPercentage = (
                      ((parseInt(row.headCountTotal) || 0) / totalEmployees) *
                      100
                    ).toFixed(1);

                    return (
                      <tr
                        key={row.group}
                        className="text-center border-b border-gray-200"
                      >
                        <td className=" px-4 py-2 text-left bg-white border-l-0 text-xs leadin-xs font-medium">
                          {row.group}
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          <CTextField
                            value={row.randM}
                            onChange={(e) =>
                              handleInputChange(idx, "randM", e.target.value)
                            }
                            className="w-full text-center"
                            placeholder="0"
                          />
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          <CTextField
                            value={row.randF}
                            onChange={(e) =>
                              handleInputChange(idx, "randF", e.target.value)
                            }
                            className="w-full text-center"
                            placeholder="0"
                          />
                        </td>
                        <td className=" px-4 py-2 bg-gray-50 text-xs leadin-xs">
                          <span className="block w-full text-center">
                            {parseFloat(row.randTotal || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-2 bg-white border-t border-b border-gray-200"></td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          <CTextField
                            value={row.headCountM}
                            onChange={(e) => {
                              const value = e.target.value;
                              const regex = /^\d*$/;
                              if (regex.test(value) || value === "") {
                                handleInputChange(idx, "headCountM", value);
                              }
                            }}
                            className="w-full text-center"
                            placeholder="0"
                          />
                        </td>
                        <td className=" px-4 bg-gray-50 py-2 text-xs leadin-xs">
                          <CTextField
                            value={row.headCountF}
                            onChange={(e) => {
                              const value = e.target.value;
                              const regex = /^\d*$/;
                              if (regex.test(value) || value === "") {
                                handleInputChange(idx, "headCountF", value);
                              }
                            }}
                            className="w-full text-center"
                            placeholder="0"
                          />
                        </td>
                        <td className=" px-4 py-2 bg-gray-50 text-xs leadin-xs">
                          <span className="block w-full text-center">
                            {row.headCountTotal}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="font-semibold bg-blue-50">
                    <td className=" px-4 py-2  text-xs leadin-xs border-l-blue-100">
                      Total
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {parseFloat(budgetTotals.randM || 0).toFixed(2)}
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {parseFloat(budgetTotals.randF || 0).toFixed(2)}
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {parseFloat(budgetTotals.randTotal || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 bg-white  border-gray-200"></td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {budgetTotals.headCountM}
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {budgetTotals.headCountF}
                    </td>
                    <td className=" px-4 py-2 text-xs leadin-xs text-center">
                      {budgetTotals.headCountTotal}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          {/* Disability Input Section */}
          <div className="mt-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
            <div className="flex flex-wrap items-end gap-6">
              <div className="min-w-[200px] flex-1 max-w-md">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Disability Budget
                  <Tooltip
                    title="It is recommended that 0.35% of the total annual organizational budget be specifically allocated towards disability inclusion and support initiatives"
                    arrow
                    placement="top"
                    className="text-xs leading-xs font-semibold"
                    sx={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#e5f3fa",
                        fontWeight: "600",
                      },
                    }}
                  >
                    <div className="cursor-pointer">
                      <InfoIcon className="w-5 h-5 text-blue-500" />
                    </div>
                  </Tooltip>
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-[calc(50%-12px)] z-[10]">
                    R
                  </span>
                  <CTextField
                    value={disabilityData.randTotal}
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = /^\d*\.?\d*$/;
                      if (regex.test(value) || value === "") {
                        const parts = value.split(".");
                        if (!parts[1] || parts[1].length <= 2) {
                          setDisabilityData((prev) => ({
                            ...prev,
                            randTotal: value,
                          }));
                        }
                      }
                    }}
                    className="w-full pl-8 border border-gray-200 rounded-lg"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="min-w-[200px] flex-1 max-w-md">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  None (unemployed) budget
                  <Tooltip
                    title="Amount allocated for the virtual None / unemployed branch. This cap appears on the dashboard B-BBEE chart and next to Unemployed investment."
                    arrow
                    placement="top"
                    className="text-xs leading-xs font-semibold"
                    sx={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#e5f3fa",
                        fontWeight: "600",
                      },
                    }}
                  >
                    <div className="cursor-pointer">
                      <InfoIcon className="w-5 h-5 text-blue-500" />
                    </div>
                  </Tooltip>
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-[calc(50%-12px)] z-[10]">
                    R
                  </span>
                  <CTextField
                    value={noneData.randTotal}
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = /^\d*\.?\d*$/;
                      if (regex.test(value) || value === "") {
                        const parts = value.split(".");
                        if (!parts[1] || parts[1].length <= 2) {
                          setNoneData((prev) => ({
                            ...prev,
                            randTotal: value,
                          }));
                        }
                      }
                    }}
                    className="w-full pl-8 border border-gray-200 rounded-lg"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="min-w-[200px] flex-1 max-w-md [&_.MuiInputBase-root.Mui-disabled]:!opacity-100 [&_.MuiInputBase-input.Mui-disabled]:!text-gray-800 [&_.MuiInputBase-input.Mui-disabled]:[-webkit-text-fill-color:rgb(31_41_55)] [&_.MuiInputBase-root]:cursor-default">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Black Budget ({blackBudgetLabel})
                </label>
                <CTextField
                  value={formatZAR(totalBlackBudgetRand)}
                  disabled
                  className="w-full border border-gray-200 rounded-lg"
                />
              </div>
              <div className="min-w-[200px] flex-1 max-w-md [&_.MuiInputBase-root.Mui-disabled]:!opacity-100 [&_.MuiInputBase-input.Mui-disabled]:!text-gray-800 [&_.MuiInputBase-input.Mui-disabled]:[-webkit-text-fill-color:rgb(31_41_55)] [&_.MuiInputBase-root]:cursor-default">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Other Budget ({otherBudgetLabel})
                </label>
                <CTextField
                  value={formatZAR(totalOtherBudgetRand)}
                  disabled
                  className="w-full border border-gray-200 rounded-lg"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <CButton
              onClick={() => methods.handleSubmit(onSubmit)()}
              className="bg-blue-500 hover:bg-blue-700 text-white px-6 py-2 rounded-lg border border-blue-500"
              disabled={loading}
            >
              Save Configuration
            </CButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BBBudgetConfiguration;
